import { useMemo } from 'react';

import {
   ConfidentialResourceAccessRequest,
   ConfidentialResourceAccessRequestQuery,
} from '@dreryk/egabinet-medical-data-confidentiality-frontend-api';

import {
   createDynamicColumnsWidth,
   JustifyContent,
   TableColumnConfiguration,
   TextPlaceholder,
} from '@dreryk/react-components';
import { dateTimeMinuteFormatter, practitionerNameFormatter } from '@dreryk/react-common';
import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';

import { systemActivityTranslationKeys } from '../translations';

import { ResourceTypeCell } from './cells/ResourceTypeCell';
import {
   isConfidentialResourceAccessRequestSingle,
   isMedicalDocumentationGeneration,
} from './cells/ResourceTypeCell.utils';
import { MedicalDocumentationGenerationResourceTypeCell } from './cells/MedicalDocumentationGenerationResourceTypeCell';
import { DataAuthorCell } from './cells/DataAuthorCell';
import { OptionsCell } from './cells/OptionsCell/OptionsCell';
import { AuthorizerCell } from './cells/AuthorizerCell';
import { DecisionDateCell } from './cells/DecisionDateCell';
import DecisionJustificationCell from './cells/DecisionJustificationCell';

export const useConfidentialResourceRequestsAccessTableDataConfiguration = (): LazyLoadedDataConfigurationByCqrsQuery<ConfidentialResourceAccessRequest> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<ConfidentialResourceAccessRequest>(() => {
      return new ConfidentialResourceAccessRequestQuery();
   });
};

/* eslint-disable max-lines-per-function */
export const useConfidentialResourceRequestsAccessTableColumnsConfiguration = (): TableColumnConfiguration<ConfidentialResourceAccessRequest>[] => {
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         grantee: '11%',
         createdAt: '11%',
         resourceType: '15%',
         owner: '13%',
         decisionDate: '12%',
         authorizer: '13%',
         decisionJustification: '12%',
         accessValidTo: '13%',
         options: '155px',
      });

      return [
         {
            name: 'grantee',
            label: systemActivityTranslationKeys.requestingPractitioner,
            accessor: 'grantee',
            width: columnsWidth.grantee,
            formatter: practitionerNameFormatter,
            sortingDisabled: true,
         },
         {
            name: 'createdAt',
            label: systemActivityTranslationKeys.requestSentDate,
            accessor: 'createdAt',
            width: columnsWidth.createdAt,
            formatter: dateTimeMinuteFormatter,
            sortingDisabled: true,
         },
         {
            name: 'resourceType',
            label: systemActivityTranslationKeys.dataCategory,
            accessor: 'resourceType',
            width: columnsWidth.resourceType,
            renderer: ({ bean }) =>
               isConfidentialResourceAccessRequestSingle(bean) && !isMedicalDocumentationGeneration(bean) ? (
                  <ResourceTypeCell confidentialResourceAccessRequest={bean} />
               ) : (
                  <MedicalDocumentationGenerationResourceTypeCell confidentialResourceAccessRequest={bean} />
               ),
            sortingDisabled: true,
         },
         {
            name: 'owner',
            label: systemActivityTranslationKeys.dataAuthor,
            accessor: 'confidentialResources',
            width: columnsWidth.owner,
            renderer: ({ bean }) => <DataAuthorCell request={bean} />,
            sortingDisabled: true,
         },
         {
            name: 'decisionDate',
            label: systemActivityTranslationKeys.accessGrantedDate,
            accessor: 'decisionDate',
            width: columnsWidth.decisionDate,
            renderer: ({ bean }) => <DecisionDateCell request={bean} />,
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'authorizer',
            label: systemActivityTranslationKeys.grantingPractitioner,
            accessor: 'authorizer',
            width: columnsWidth.authorizer,
            renderer: ({ bean }) => {
               return (
                  <AuthorizerCell
                     authorizer={bean.authorizer || bean.requestDeniedBy}
                     grantee={bean.grantee}
                     accessLevel={bean.accessLevel}
                  />
               );
            },
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'decisionJustification',
            label: systemActivityTranslationKeys.reason,
            accessor: 'decisionJustification',
            width: columnsWidth.decisionJustification,
            renderer: ({ bean }) =>
               bean.decisionJustification ? (
                  <DecisionJustificationCell decisionJustification={bean.decisionJustification} />
               ) : null,
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'accessValidTo',
            label: systemActivityTranslationKeys.accessExpirationDate,
            accessor: 'accessValidTo',
            width: columnsWidth.accessValidTo,
            formatter: dateTimeMinuteFormatter,
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'options',
            accessor: 'id',
            width: columnsWidth.options,
            justifyContent: JustifyContent.END,
            sortingDisabled: true,
            renderer: ({ bean }) => {
               return <OptionsCell request={bean} />;
            },
         },
      ];
   }, []);
};
